import { useCallback, useRef } from 'react';
import { Outlet } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Topbar } from '@/components/topbar';
import { MobileNav } from '@/components/mobile-nav';
import { useAlertSocket } from '@/hooks/use-socket';
import { DeployOverlay } from '@/components/deploy-overlay';
import { useDeployStatus } from '@/stores/deploy-status';
import type { Alert } from '@/types/api';

const BATCH_WINDOW_MS = 2000;

function showToast(severity: string, title: string, description: string) {
  const opts = { description, duration: 6000 };
  if (severity === 'CRITICAL') toast.error(title, opts);
  else if (severity === 'WARNING') toast.warning(title, opts);
  else toast.info(title, opts);
}

export default function AppLayout() {
  const queryClient = useQueryClient();
  const batchRef = useRef<Map<string, Alert[]>>(new Map());
  const timerRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const flushBatch = useCallback((key: string) => {
    const batch = batchRef.current.get(key);
    timerRef.current.delete(key);
    batchRef.current.delete(key);
    if (!batch || batch.length === 0) return;

    if (batch.length === 1) {
      showToast(batch[0]!.severity, batch[0]!.title, batch[0]!.message);
    } else {
      const severity = batch.some(a => a.severity === 'CRITICAL') ? 'CRITICAL'
        : batch.some(a => a.severity === 'WARNING') ? 'WARNING' : 'INFO';
      const title = `${batch.length} ${key} alerts`;
      const desc = batch.slice(0, 3).map(a => a.title).join(', ')
        + (batch.length > 3 ? ` +${batch.length - 3} more` : '');
      showToast(severity, title, desc);
    }
  }, []);

  const onAlert = useCallback(
    (data: Alert) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });

      const key = data.category;
      const batch = batchRef.current.get(key) ?? [];
      batch.push(data);
      batchRef.current.set(key, batch);

      // First alert in batch — start timer
      if (!timerRef.current.has(key)) {
        timerRef.current.set(key, setTimeout(() => flushBatch(key), BATCH_WINDOW_MS));
      }
    },
    [queryClient, flushBatch],
  );

  useAlertSocket(onAlert);
  const deployStatus = useDeployStatus();

  return (
    <div className="flex flex-col h-screen overflow-hidden relative z-0">
      <DeployOverlay {...deployStatus} />
      {/* Torch glow — soft orange beam at top center */}
      <div
        className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1400px] h-[700px] z-0"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(249,115,22,0.08) 0%, rgba(249,115,22,0.03) 40%, transparent 70%)',
        }}
      />

      <Topbar />

      <main className="flex-1 overflow-y-auto py-6 pb-24 lg:pb-8 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      <MobileNav />
    </div>
  );
}
